import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FaceProvider } from "./contexts/FaceContext";
import HomeImproved from "./pages/HomeImproved";
import { CommandPalette } from "./components/CommandPalette";
import { KeyboardShortcutsHelp } from "./components/KeyboardShortcutsHelp";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";

// Import three-face design system
import "./styles/face-tokens.css";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import Transactions from "./pages/Transactions";
import Documents from "./pages/Documents";
import FileInbox from "./pages/FileInbox";
import Intelligence from "./pages/Intelligence";
import Statements from "./pages/Statements";
import TaxReports from "./pages/TaxReports";
import VATReport from "./pages/VATReport";
import Ratios from "./pages/Ratios";
import EnhancedBankImport from "./pages/EnhancedBankImport";
import Reconciliation from "./pages/Reconciliation";
import CategorizationRules from "./pages/CategorizationRules";
import CategorizationAnalytics from "./pages/CategorizationAnalytics";
import { RuleSuggestions } from "./pages/RuleSuggestions";
import Templates from "./pages/Templates";
import TemplateAnalytics from "./pages/TemplateAnalytics";
import TaxOptimization from "./pages/TaxOptimization";
import TaxExport from "./pages/TaxExport";
import RndProjects from "./pages/RndProjects";
import Vendors from "./pages/Vendors";
import Customers from "./pages/Customers";
import CompanyProfileSetup from "./pages/CompanyProfileSetup";
import UserProfile from "./pages/UserProfile";
import { HistoricalTax } from "./pages/HistoricalTax";
import AIUsage from "./pages/AIUsage";
import Login from "./pages/Login";
import AISettings from "./pages/AISettings";
import MonitoringDashboard from "./pages/MonitoringDashboard";
import AITest from "./pages/ai-test";
import AccountantMarketplace from "./pages/AccountantMarketplace";
import AccountantRegistration from "./pages/AccountantRegistration";
import AccountantDashboard from "./pages/AccountantDashboard";
import AccountantProfile from "./pages/AccountantProfile";
import CompanySettings from "./pages/CompanySettings";
import ApiKeys from "./pages/ApiKeys";
import ImportHistory from "./pages/ImportHistory";
import CompanyDiscovery from "./pages/CompanyDiscovery";
import OnboardingWizard from "./pages/OnboardingWizard";
import BulkImport from "./pages/BulkImport";
import CFODashboard from "./pages/CFODashboard";
import LoanApplicationGenerator from "./pages/LoanApplicationGenerator";
import SwissGAAPFER from "./pages/SwissGAAPFER";
import AccrualDetector from "./pages/AccrualDetector";
import Admin from "./pages/Admin";
import Integrations from "./pages/Integrations";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path={"/"} component={HomeImproved} />
      <Route path={"/dashboard"} component={Dashboard} />
      <Route path={"/companies"} component={Companies} />
      <Route path={"/company-discovery"} component={CompanyDiscovery} />
      {/* Deprecated: Redirect old onboarding-wizard routes to company-discovery */}
      <Route path={"/onboarding-wizard"}>
        {() => {
          window.location.replace("/company-discovery");
          return null;
        }}
      </Route>
      <Route
        path={"/onboarding-wizard/:sessionId"}
        component={OnboardingWizard}
      />
      <Route path={"/transactions"} component={Transactions} />
      <Route path={"/documents"} component={Documents} />
      <Route path={"/file-inbox"} component={FileInbox} />
      <Route path={"/intelligence"} component={Intelligence} />
      <Route path={"/statements"} component={Statements} />
      <Route path={"/tax"} component={TaxReports} />
      <Route path={"/vat"} component={VATReport} />
      <Route path="/ratios" component={Ratios} />
      <Route path="/bank-import" component={EnhancedBankImport} />
      <Route path="/bulk-import" component={BulkImport} />
      <Route path="/enhanced-import">
        {() => {
          window.location.href = "/bank-import";
          return null;
        }}
      </Route>
      <Route path="/reconciliation" component={Reconciliation} />
      <Route path="/categorization-rules" component={CategorizationRules} />
      <Route
        path="/categorization-analytics"
        component={CategorizationAnalytics}
      />
      <Route path="/rule-suggestions" component={RuleSuggestions} />
      <Route path="/templates" component={Templates} />
      <Route path="/template-analytics" component={TemplateAnalytics} />
      <Route path="/tax-optimization" component={TaxOptimization} />
      <Route path="/tax-export" component={TaxExport} />
      <Route path="/rnd-projects" component={RndProjects} />
      <Route path="/vendors" component={Vendors} />
      <Route path="/customers" component={Customers} />
      <Route path="/company-profile" component={CompanyProfileSetup} />
      <Route path="/user-profile" component={UserProfile} />
      <Route path="/ai-settings" component={AISettings} />
      <Route path="/ai-usage" component={AIUsage} />
      <Route path="/historical-tax" component={HistoricalTax} />
      <Route path="/monitoring" component={MonitoringDashboard} />
      <Route path="/ai-test" component={AITest} />
      {/* Accountant Marketplace Routes */}
      <Route path="/accountants" component={AccountantMarketplace} />
      <Route path="/accountant/register" component={AccountantRegistration} />
      <Route path="/accountant/dashboard" component={AccountantDashboard} />
      <Route path="/accountant/:id" component={AccountantProfile} />
      <Route path="/company-settings" component={CompanySettings} />
      <Route path="/api-keys" component={ApiKeys} />
      <Route path="/import-history" component={ImportHistory} />
      <Route path="/integrations" component={Integrations} />
      {/* Phase 6 - Advanced Features */}
      <Route path="/cfo-dashboard" component={CFODashboard} />
      <Route path="/loan-application" component={LoanApplicationGenerator} />
      <Route path="/swiss-gaap-fer" component={SwissGAAPFER} />
      <Route path="/accrual-detector" component={AccrualDetector} />
      {/* Admin Panel */}
      <Route path="/admin" component={Admin} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function GlobalShortcuts() {
  // Initialize global keyboard shortcuts
  useKeyboardShortcuts();
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <FaceProvider>
          <TooltipProvider>
            <Toaster />
            <CommandPalette />
            <KeyboardShortcutsHelp />
            <GlobalShortcuts />
            <Router />
          </TooltipProvider>
        </FaceProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
